/**
 * Ejected from `experimental/shared/nav/ObjectiveFilter.tsx` for NEX-18040: the controls row now
 * holds the loop switcher and the List/Details picker on the same line as the objective pills, which
 * are the only flexible-width piece — so this row has to fit what it can and push the rest into a
 * "…" menu, something the shared filter (which just wraps) has no notion of. Every other variant
 * still imports the shared, wrapping version and is unaffected.
 */
import { MoreHoriz } from '@tempo-io/sui-icons';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectiveBucket } from '@/features/agentic-insights/components/experimental/shared/data/objectiveBuckets';
import { UNASSIGNED_OBJECTIVE } from '@/features/agentic-insights/components/experimental/shared/data/unassignedObjective';
import type {
  ToneTokens,
  TypeTokens,
} from '@/features/agentic-insights/components/experimental/shared/tokens';
import { useDismissOnOutside } from '@/features/agentic-insights/components/experimental/shared/ui/useDismissOnOutside';
import { useFitPills } from './useFitPills';

interface ObjectiveFilterOverflowProps {
  type: Pick<TypeTokens, 'metaValue' | 'meta'>;
  tone: Pick<ToneTokens, 'neutral' | 'attention'>;
  buckets: ObjectiveBucket[];
  totalFindings: number;
  objective: string | undefined;
  onChange: (objective: string | undefined) => void;
}

interface PillItem {
  key: string | undefined;
  text: string;
  count: number;
  tone: string;
}

/**
 * The objective filter, fitted to one line: every pill that fits stays on the row, the rest move
 * into a "…" menu that still carries their counts, so the row never wraps and never hides a total.
 *
 * A selected objective always sorts to the front (right after "All objectives", which never moves):
 * picking one from the "…" menu must never hide the very pill it just selected.
 */
export const ObjectiveFilterOverflow = ({
  type,
  tone,
  buckets,
  totalFindings,
  objective,
  onChange,
}: ObjectiveFilterOverflowProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  useDismissOnOutside(open, containerRef, () => setOpen(false));

  const objectiveTitle = (bucket: ObjectiveBucket) =>
    bucket.key === UNASSIGNED_OBJECTIVE
      ? t(($) => $.loopAgenticInsights.experimental.objectives.unassigned)
      : (bucket.title ?? t(($) => $.loopAgenticInsights.reference.objective, { id: bucket.key }));

  const rest: PillItem[] = buckets
    .filter((bucket) => bucket.findingCount > 0)
    .map((bucket) => ({
      key: bucket.key,
      text: objectiveTitle(bucket),
      count: bucket.findingCount,
      tone: bucket.attentionCount > 0 ? tone.attention : tone.neutral,
    }));

  // The selected pill always rides to the front of the flexible part of the row (but never ahead of
  // "All objectives", which is a fixed lead pill and not part of this sort) — see the module header.
  rest.sort((a, b) => (b.key === objective ? 1 : 0) - (a.key === objective ? 1 : 0));

  const items: PillItem[] = [
    {
      key: undefined,
      text: t(($) => $.loopAgenticInsights.experimental.objectives.allObjectives),
      count: totalFindings,
      tone: tone.neutral,
    },
    ...rest,
  ];

  const {
    containerRef: fitRef,
    moreMeasureRef,
    itemRef,
    visibleCount,
    overflowed,
    // Order (the selected pill riding to the front) and labels (a title resolving from a
    // placeholder) both change without `items.length` changing — see `useFitPills`'s own comment.
  } = useFitPills(items.length, items.map((item) => `${item.key ?? 'all'}:${item.text}`).join('|'));
  const hiddenItems = overflowed ? items.slice(visibleCount) : [];
  const selectedIsHidden = hiddenItems.some((item) => item.key === objective);
  // The widest the "…" pill's count could ever read, for the measuring clone below — not the
  // *current* `hiddenItems.length`, which is itself downstream of the very reservation being
  // computed from it: fewer pills fit → a bigger hidden count → a wider clone → a smaller budget →
  // fewer pills fit, a feedback loop with no stable fixed point. At most every pill but one can end
  // up hidden, so that count's digit width is always enough headroom regardless of what actually
  // ends up hidden.
  const maxPossibleHiddenCount = Math.max(items.length - 1, 1);

  const pillClass = (isSelected: boolean) =>
    `flex items-baseline gap-1.5 rounded-full border px-2.5 py-0.5 text-xs whitespace-nowrap transition-colors ${
      isSelected
        ? 'border-border-brand bg-bg-brand-subtler font-medium text-text-neutral-primary'
        : 'border-border-neutral-subtle text-text-neutral-secondary hover:border-border-neutral-bold hover:text-text-neutral-primary'
    }`;

  const select = (key: string | undefined) => {
    onChange(objective === key ? undefined : key);
    setOpen(false);
  };

  return (
    // `relative`, so the "…" trigger below can sit out of the flex flow (`absolute`) rather than as
    // a `shrink-0` sibling — a real sibling would shrink the fieldset's own measured width, which
    // `fitPills` already reserves room for on its own terms; keeping both would reserve it twice
    // and hide one more pill than necessary. Positioning it here instead means the fieldset's
    // measured width is the row's real, undiminished width in every pass, overflowed or not.
    <div className="relative flex min-w-0 flex-1 items-center gap-1.5">
      <fieldset
        ref={fitRef}
        aria-label={t(($) => $.loopAgenticInsights.experimental.objectives.label)}
        data-testid="agentic-x-objective-filter"
        className="flex min-w-0 flex-1 flex-nowrap items-center gap-1.5 overflow-hidden border-0 p-0"
      >
        {items.map((item, index) => {
          const isSelected = objective === item.key;
          return (
            <button
              key={item.key ?? 'all'}
              ref={itemRef(index)}
              type="button"
              aria-pressed={isSelected}
              data-testid={
                item.key === undefined ? 'agentic-x-objective-all' : 'agentic-x-objective'
              }
              onClick={() => select(item.key)}
              className={pillClass(isSelected)}
            >
              <span className="max-w-56 truncate">{item.text}</span>
              <span className={`${type.metaValue} ${item.tone}`}>{item.count}</span>
            </button>
          );
        })}
      </fieldset>

      {/* Always mounted, never shown: measured for its width before the fit decision runs so the
          reservation exists whether or not overflow ends up needed. */}
      <button
        ref={moreMeasureRef}
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="pointer-events-none invisible absolute flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs"
      >
        <MoreHoriz className="size-3.5" aria-hidden="true" />
        <span className={type.metaValue}>{'9'.repeat(String(maxPossibleHiddenCount).length)}</span>
      </button>

      {overflowed ? (
        <span ref={containerRef} className="absolute inset-y-0 right-0 flex items-center">
          <button
            type="button"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={open ? panelId : undefined}
            aria-label={t(($) => $.loopAgenticInsights.experimental.objectives.moreLabel, {
              count: hiddenItems.length,
            })}
            data-testid="agentic-x-objective-more"
            onClick={() => setOpen((current) => !current)}
            className={`flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors hover:border-border-neutral-bold ${
              selectedIsHidden
                ? 'border-border-brand text-text-brand-primary'
                : 'border-border-neutral-subtle text-text-neutral-secondary'
            }`}
          >
            <MoreHoriz className="size-3.5" aria-hidden="true" />
            <span className={type.metaValue}>{hiddenItems.length}</span>
          </button>
          {open ? (
            <span
              id={panelId}
              role="listbox"
              aria-label={t(($) => $.loopAgenticInsights.experimental.objectives.label)}
              data-testid="agentic-x-objective-more-menu"
              className="absolute top-full right-0 z-20 mt-2 flex max-h-80 w-72 flex-col overflow-y-auto rounded-md border border-border-neutral bg-bg-surface-overlay py-1 shadow-lg"
            >
              {hiddenItems.map((item) => (
                <button
                  key={item.key ?? 'all'}
                  type="button"
                  role="option"
                  aria-selected={objective === item.key}
                  onClick={() => select(item.key)}
                  className={`flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-bg-neutral-subtle-hovered ${
                    objective === item.key ? 'bg-bg-brand-subtler text-text-brand-primary' : ''
                  }`}
                >
                  <span className="truncate">{item.text}</span>
                  <span className={`${type.metaValue} ${item.tone}`}>{item.count}</span>
                </button>
              ))}
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
};
